import type { ComponentPropsWithoutRef } from 'react'
import { forwardRef } from 'react'

import { ConditionalWrapWithTooltipCompat as ConditionalWrapWithTooltip } from '@adverity/ds-core/tooltip/compat'
import { useIsTruncating } from '@adverity/ds-core/use-is-truncating'
import { cn } from '@adverity/ds-tokens/cn'
import { Slot } from '@radix-ui/react-slot'

type TextStyle = 'title' | 'body' | 'description' | 'details' | 'inherit'

type TextColor =
    | 'primary'
    | 'secondary'
    | 'muted'
    | 'inverted'
    | 'inverted-secondary'
    | 'link'
    | 'danger'
    | 'success'
    | 'warning'
    | 'brand'
    | 'inherit'

type TextElement =
    | 'span'
    | 'p'
    | 'div'
    | 'label'
    | 's'
    | 'em'
    | 'strong'
    | 'small'
    | 'time'
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'li'
    | 'caption'
    | 'abbr'
    | 'code'
    | 'dt'
    | 'dd'
    | 'legend'

type TextWeight = 'regular' | 'semi-bold' | 'bold'

type TextAlign = 'left' | 'center' | 'right'

type TextProps = Omit<ComponentPropsWithoutRef<'span'>, 'style' | 'color'> & {
    as?: TextElement
    asChild?: boolean
    textStyle?: TextStyle
    color?: TextColor
    weight?: TextWeight
    align?: TextAlign
    truncate?: number
}

const textStyleMap: Record<TextStyle, string> = {
    title: 'text-medium leading-medium',
    body: 'text-small leading-small',
    description: 'text-x-small leading-x-small',
    details: 'text-xx-small leading-xx-small',
    inherit: '',
}

const weightMap: Record<TextWeight, string> = {
    regular: 'font-regular',
    'semi-bold': 'font-semi-bold',
    bold: 'font-bold',
}

const alignMap: Record<TextAlign, string> = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
}

const colorMap: Record<TextColor, string> = {
    primary: 'text-text',
    secondary: 'text-text-secondary',
    muted: 'text-text-muted',
    inverted: 'text-text-inverted',
    'inverted-secondary': 'text-text-inverted-secondary',
    link: 'text-text-link',
    danger: 'text-danger-active',
    success: 'text-success',
    warning: 'text-warning-active',
    brand: 'text-brand',
    inherit: 'text-current',
}

function getBaseClasses(textStyle: TextStyle) {
    return textStyle === 'inherit' ? '' : 'font-body font-regular'
}

function resolveColor(color: TextColor | undefined, textStyle: TextStyle) {
    return color ?? (textStyle === 'description' ? 'secondary' : 'inherit')
}

const Text = forwardRef<HTMLElement, TextProps>(
    (
        { as = 'span', asChild, textStyle = 'body', color, weight, align, truncate, className, children, ...props },
        ref,
    ) => {
        const resolvedColor = resolveColor(color, textStyle)
        const Component = asChild ? Slot : as
        const mergedClassName = cn(
            getBaseClasses(textStyle),
            truncate && 'overflow-hidden focus-visible:outline-2 focus-visible:outline-current',
            textStyleMap[textStyle],
            colorMap[resolvedColor],
            weight && weightMap[weight],
            align && alignMap[align],
            className,
        )

        if (truncate) {
            return (
                <TruncatedText
                    Component={Component}
                    ref={ref}
                    className={mergedClassName}
                    truncate={truncate}
                    {...props}
                >
                    {children}
                </TruncatedText>
            )
        }

        return (
            <Component ref={ref as never} className={mergedClassName} {...props}>
                {children}
            </Component>
        )
    },
)

type TruncatedTextProps = Omit<ComponentPropsWithoutRef<'span'>, 'style' | 'color'> & {
    Component: typeof Slot | TextElement
    truncate: number
}

const TruncatedText = forwardRef<HTMLElement, TruncatedTextProps>(
    ({ Component, truncate, className, children, ...props }, ref) => {
        const { truncateContentRef, isTruncating, textContent } = useIsTruncating({
            truncateLines: truncate,
            children,
        })

        return (
            <ConditionalWrapWithTooltip tooltip={isTruncating ? textContent : undefined}>
                <Component
                    ref={ref as never}
                    className={cn('[-webkit-box-orient:vertical] [display:-webkit-box]', className)}
                    style={{ WebkitLineClamp: truncate }}
                    tabIndex={isTruncating ? 0 : -1}
                    {...props}
                >
                    <span ref={truncateContentRef as never}>{children}</span>
                </Component>
            </ConditionalWrapWithTooltip>
        )
    },
)

TruncatedText.displayName = 'TruncatedText'

Text.displayName = 'Text'

export { Text }
export type { TextProps, TextStyle, TextColor, TextElement, TextWeight, TextAlign }
